import React from 'react'
import styled from 'styled-components'
import {
  Button,
  FormError,
  IconArrowRight,
  PromoCode,
  PromoCodeList,
  Typography,
} from '../../../Common'
import { HippoCard } from './Components'
import {
  usePrice,
  useOrderPrice,
  useOrderShippingPrice,
  useCheckoutFormServerErrors,
  useUIFunctions,
  useConfigState,
  useGlobalLabels,
  useLocalLabels,
  useAllSubscriptions,
} from '@sentecacommerce-theme/lib'
import { CheckoutLabels } from '../../checkout.props'

import { Checkbox } from '../Checkbox'

export const OrderSummary = ({
  title,
  gdprAgreed,
  checkGDPR,
  uncheckGDPR,
}: {
  title: string
  gdprAgreed: boolean
  checkGDPR: () => void
  uncheckGDPR: () => void
}) => {
  const { enqueueView } = useUIFunctions()
  const config = useConfigState()
  const { subtotal, taxedPrice, discountCodesSum } = useOrderPrice()

  const { promoCodeLabels, errorMessages } = useGlobalLabels()
  const labels = useLocalLabels<CheckoutLabels>()
  const {
    originalPrice: shippingPrice,
    discountedPrice: shippingDiscountedPrice,
  } = useOrderShippingPrice()

  const {
    requiredSubs,
    checkedSubs,
    clickSub,
    allSubscriptions,
    checkRequiredSubs,
  } = useAllSubscriptions({ tag: 'checkout' })

  const { price: subtotalFormatted } = usePrice({
    price: subtotal,
    discountedPrice: undefined,
  })

  const { price: totalWithShippingFormatted } = usePrice({
    price: taxedPrice,
    discountedPrice: undefined,
  })

  const { price: shippingFormatted } = usePrice({
    price: shippingDiscountedPrice || shippingPrice,
    discountedPrice: undefined,
  })

  const { price: discountAmount } = usePrice({
    price: discountCodesSum,
    discountedPrice: undefined,
  })

  const { serverErrors } = useCheckoutFormServerErrors()

  const { currency } = subtotalFormatted

  const handleClick = (sub: string) => {
    clickSub(sub)
    const allChecked = checkRequiredSubs()
    if (!allChecked) {
      uncheckGDPR()
    } else {
      checkGDPR()
    }
  }

  return (
    <Container>
      <Left>
        <Flex>
          <Title>{title}</Title>
          <ViewItems
            onClick={() => enqueueView(config.views.checkoutBasketPreview)}
          >
            {labels?.previewItemsLabel}
            <IconArrowRight />
          </ViewItems>
        </Flex>
        <Row>
          <Name bigFont>{labels?.totalWithoutShippingPrice}:</Name>
          <Value bigFont>{subtotalFormatted.displayValue}</Value>
        </Row>
        <Row>
          <Name bigFont>{labels?.totalShippingPrice}:</Name>
          <Value bigFont>
            {!shippingPrice
              ? labels?.shippingPriceNotAvailable
              : `${shippingFormatted.displayValue}`}
          </Value>
        </Row>
        {discountAmount.value > 0 && (
          <Row>
            <Name bigFont>
              <strong>{promoCodeLabels?.promoCodeBoxTitle}</strong>
            </Name>
            <Value bigFont>
              <strong>
                -{discountAmount?.value?.toFixed(2)} {currency}
              </strong>
            </Value>
          </Row>
        )}
        <GreyLine></GreyLine>
        <Row big>
          <Name big>{labels?.totalPrice}:</Name>
          <Value big>{totalWithShippingFormatted.displayValue}</Value>
        </Row>
      </Left>
      <Spacer />
      <Right>
        <PromoCodeList />
        <PromoCodeContainer>
          <PromoCode />
          <div className="pt-2">
            <HippoCard />
          </div>
        </PromoCodeContainer>
        <BtnSecurityNoticeContainer>
          <GDPRConsentWrapper>
            {requiredSubs.map((sub: string) => (
              <Checkbox
                key={sub}
                onChange={() => handleClick(sub)}
                isChecked={checkedSubs?.[sub]}
                size="tiny"
              >
                <CheckboxLabel>{allSubscriptions?.[sub].label}</CheckboxLabel>
              </Checkbox>
            ))}
          </GDPRConsentWrapper>
          <Button
            type="submit"
            variant={!gdprAgreed ? 'disabled' : 'green'}
            disabled={!gdprAgreed}
            borderRadius="small"
          >
            {labels?.submitButtonLabel}
          </Button>

          {/* <Typography variant="9" color="primary" className="mt-2">
            {labels?.securityNotice}
          </Typography> */}
        </BtnSecurityNoticeContainer>
        {Boolean(serverErrors) && (
          <FormError text={errorMessages?.somethingWentWrong} />
        )}
      </Right>
    </Container>
  )
}

const Title = styled(Typography).attrs({
  as: 'h2',
  variant: 3,
})`
  font-weight: normal;
  margin-right: 0.4rem;
  font-size: 2.1rem;
`

const Flex = styled.div.attrs({
  className: 'mb-4 pb-2',
})`
  display: flex;
  align-items: center;
  width: 100%;
`

const GDPRConsentWrapper = styled.div`
  width: 100%;

  margin-top: 2.6rem;
  margin-bottom: 2.6rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    margin-top: 1.7rem;
    margin-bottom: 1.7rem;
  }
`

const CheckboxLabel = styled.span`
  display: block;
  margin-left: 0.4rem;
  font-size: 1.7rem;

  & span {
    margin-top: 0;
  }
`

const ViewItems = styled.button.attrs({
  type: 'button',
  className: 'p-0 ml-3',
})`
  color: ${({ theme }) => theme.palette.silverTree};
  font-size: 1.6rem;
  min-height: 0 !important;
  width: auto !important;
  margin-left: 0.8rem;
  margin-top: 0.37rem;

  display: flex;
  align-items: center;

  & svg {
    width: 0.5rem;
    margin-left: 0.7rem;
  }

  &:hover {
    opacity: 1 !important;
    background: none !important;
    text-decoration: underline;
  }
`

const Left = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    max-width: calc(100% - 52rem);
  }
`

const PromoCodeContainer = styled.div.attrs({
  className: 'p-4',
})`
  border-radius: 0.4rem;

  background-color: ${({ theme }) => theme.palette.panache};
`

const Right = styled.div.attrs({
  className: 'mt-5 mt-sm-auto pt-sm-4',
})`
  width: 42rem;

  & button {
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    width: 100%;
  }
`

const Container = styled.div.attrs({
  className: 'd-flex justify-content-between',
})`
  flex-wrap: wrap;
`

const Spacer = styled.div.attrs({
  className: 'mx-5 d-none d-lg-block',
})`
  min-width: 0.1rem;
  max-width: 0.1rem;
  height: 15.4rem;
  background: ${({ theme }) => theme.palette.disabled};
`

const Row = styled.div.attrs({
  className: 'd-flex justify-content-between',
})<{ big?: boolean }>`
  width: 100%;
  padding-top: ${(props) => (props.big ? '1rem' : 0)};
  padding-bottom: 1.2rem;

  color: ${(props) => (props.big ? props.theme.palette.primary : 'inherit')};
`

const Name = styled.span<{ big?: boolean; bigFont?: boolean }>`
  font-weight: ${(props) => (props.big ? '600' : '300')};
  font-size: ${(props) => (props.big ?? props.bigFont ? '1.8rem' : 'inherit')};
  line-height: 2.2rem;
`

const Value = styled.span<{ big?: boolean; bigFont?: boolean }>`
  font-weight: ${(props) => (props.big ? '600' : '400')};
  font-size: ${(props) => (props.big ?? props.bigFont ? '1.8rem' : 'inherit')};
  line-height: 2.2rem;
`

const GreyLine = styled.span.attrs({ className: 'my-5' })`
  &:before {
    content: '';
    width: 100%;
    display: block;
    border-top: 0.1rem solid ${({ theme }) => theme.palette.lightgrey};
    margin-top: 1rem;
  }
`
const BtnSecurityNoticeContainer = styled.div.attrs({
  className: 'd-flex flex-column align-items-center',
})`
  & button {
    font-size: 2.2rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
      font-size: 2.6rem;
    }
  }

  & p {
    font-size: 1.4rem;
  }
`
